import type { NewUserFormData } from "@/schemas/users/new-user";
import type { ApiUserResponseType } from "@/types/entities/users";
import axios from "@/utils/axios";

import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export type UsersFiltersProps = {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  role?: string;
  position?: string;
  institution?: string;
  withoutRole?: boolean;
  sort_direction?: "asc" | "desc";
  relationships?: string[];
  processes?: string;
  created_by?: string;
  group_by?: string;
  status?: string;
  created_at?: string;
};

export const useGetUsers = (filters: UsersFiltersProps) => {
  const { relationships = [], ...restFilters } = filters;
  const queryParams = {
    page: restFilters.page || 1,
    per_page: restFilters.per_page || 10,
    order_by: restFilters.group_by || "!created_at",
    filter: {
      ...(restFilters.search && { search: restFilters.search }),
      ...(restFilters.role && { "userRoles.role_id": restFilters.role }),
      ...(restFilters.position && {
        registered_position_id: restFilters.position,
      }),
      ...(restFilters.institution && {
        assigned_institution_id: restFilters.institution,
      }),
      ...(restFilters.status && { user_status: restFilters.status }),
      ...(restFilters.created_at && { created_at: restFilters.created_at }),
    },
  };

  const queryKey = { ...queryParams, relationships };

  return useQuery({
    queryKey: ["users", queryKey],
    queryFn: async (): Promise<ApiUserResponseType> => {
      const response = await axios.get("/admin/v1/users", {
        params: {
          ...queryParams,
          ...(relationships.length > 0 && {
            relationships: relationships.join(","),
          }),
        },
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const useGetUsersAsInfinite = (
  filters: UsersFiltersProps,
  enabled = true,
) => {
  const { relationships = [], ...restFilters } = filters;

  const queryParams = {
    per_page: restFilters.per_page || 10,
    order_by: restFilters.group_by || "created_at",
    filter: {
      ...(restFilters.search && { search: restFilters.search }),
      ...(restFilters.withoutRole && { userRoles: "?" }),
      ...(restFilters.role &&
        !restFilters.withoutRole && { "userRoles.role_id": restFilters.role }),
      ...(restFilters.position && {
        "position.id": restFilters.position,
      }),
      ...(restFilters.institution && {
        "institution.id": restFilters.institution,
      }),
      ...(restFilters.created_by && { created_by: restFilters.created_by }),
      ...(restFilters.status && { user_status: restFilters.status }),
    },
  };

  const queryKey = { ...queryParams, relationships };

  return useInfiniteQuery({
    queryKey: ["users-infinite", queryKey],
    queryFn: async ({ pageParam = 1 }): Promise<ApiUserResponseType> => {
      const response = await axios.get("/admin/v1/users", {
        params: {
          ...queryParams,
          ...(relationships.length > 0 && {
            relationships: relationships.join(","),
          }),
          page: pageParam,
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (lastPage as ApiUserResponseType)
        .meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
    enabled,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: NewUserFormData) => {
      const response = await axios.post("/admin/v1/users", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
    },
  });
};

export const useUpdateUser = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: NewUserFormData) => {
      const response = await axios.put(`/admin/v1/users/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["user", id],
      });
    },
  });
};

export const useActivateUser = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { description: string }) => {
      const response = await axios.patch(
        `/admin/v1/users/${id}/reactive-account`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["user", id],
      });
    },
  });
};

export const useInactivateUser = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { description: string }) => {
      const response = await axios.patch(
        `/admin/v1/users/${id}/inactive-account`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["user", id],
      });
    },
  });
};

const getUser = async (id: string) => {
  const response = await axios.get(
    `/admin/v1/users/${id}?relationships=userRoles`,
  );
  return response.data;
};

export const useGetUser = (id: string) => {
  return useQuery({
    queryKey: ["user", id],
    queryFn: async (): Promise<ApiUserResponseType["data"][number]> => {
      const { data } = await getUser(id);
      return data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export {
  useGetUserPositions,
  useGetUserOrganizations,
  useGetUserPositionsAsInfinite,
  useGetUserOrganizationsAsInfinite,
  useGetUserUnits,
} from "./users.catalogs.api";

export const useAssignUserToRole = (roleId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (usersId: string[]) => {
      const response = await axios.put(`/admin/v1/users/assign-group`, {
        users: usersId,
        group_id: roleId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["role", roleId],
      });
    },
  });
};

export const useUnassignUserToRole = (roleId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (usersId: string[]) => {
      const response = await axios.patch(`/admin/v1/users/unassign-group`, {
        users: usersId,
        group_id: roleId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["role", roleId],
      });
    },
  });
};

export const useResetUserPassword = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await axios.patch(
        `/admin/v1/users/${id}/change-password`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["user", id],
      });
    },
  });
};

export const useUnblockUser = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await axios.patch(
        `/admin/v1/users/${id}/unlock-account`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["users"],
      });
      queryClient.invalidateQueries({
        queryKey: ["user", id],
      });
    },
  });
};

export const createUserDetailsBreadcrumb = (
  queryClient: QueryClient,
  id: string,
) => {
  return async () => {
    const userId = id;
    try {
      const user = await queryClient.ensureQueryData({
        queryKey: ["user", userId],
        queryFn: async () => {
          const response = await getUser(userId);
          return response.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
      return {
        data: {
          ...user,
          name: `${user.name || ""} ${user.last_name || ""}`,
        },
      };
    } catch (error) {
      console.error("Error fetching role for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};

export type ApplicantUsersFiltersProps = {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  name?: string;
  document_number?: string;
  email?: string;
};

export const useGetApplicantUsersAsInfinite = (
  filters?: ApplicantUsersFiltersProps,
  enabled = true,
) => {
  const queryParams = {
    per_page: filters?.per_page || 20,
    order_by: filters?.sort_by || "created_at",
    filter: {
      ...(filters?.search && { search: filters.search }),
      ...(filters?.document_number && {
        document_number: filters.document_number,
      }),
      ...(filters?.email && { email: filters.email }),
      ...(filters?.name && { name: filters.name }),
    },
  };
  return useInfiniteQuery({
    queryKey: ["applicant-users-infinite", queryParams],
    queryFn: async ({ pageParam = 1 }): Promise<ApiUserResponseType> => {
      const response = await axios.get("/admin/v1/applicant", {
        params: { ...queryParams, page: pageParam },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (lastPage as ApiUserResponseType)
        .meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
    enabled,
  });
};
